import { format, isValid } from "date-fns";
import * as Sentry from "@sentry/react";

export const CANDIDATE_DATE_FORMAT = "yyyy-MM-dd HH:mm";

/** Shown wherever a candidate date is missing or unparseable. */
export const UNKNOWN_DATE_LABEL = "datum onbekend";

interface CandidateDateContext {
  candidateId: string | number | undefined;
  field: "createDate" | "visitDate";
}

// One report per record + field per page load. The block re-renders often and a
// single broken record must not flood Sentry.
const reported = new Set<string>();

/**
 * Turn a candidate date (Date, ISO string, null, undefined) into a Date, or
 * null when there is nothing usable. Never returns an Invalid Date.
 */
export function toCandidateDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value as string | number);
  return isValid(date) ? date : null;
}

/**
 * Format a candidate date for display. Returns `UNKNOWN_DATE_LABEL` instead of
 * letting date-fns throw `RangeError: Invalid time value`, which took the whole
 * dashboard down (Sentry, 2026-10). A value that is present but unparseable is
 * reported, because that points at a broken record in the backend; a missing
 * value is an expected state (no appointment yet) and stays quiet.
 */
export function formatCandidateDate(value: unknown, context: CandidateDateContext): string {
  const date = toCandidateDate(value);
  if (date) return format(date, CANDIDATE_DATE_FORMAT);

  const isPresentButBroken = value !== null && value !== undefined && value !== "";
  const key = `${context.candidateId ?? "unknown"}:${context.field}`;
  if (isPresentButBroken && !reported.has(key)) {
    reported.add(key);
    Sentry.captureMessage("Candidate has an unparseable date", {
      level: "warning",
      tags: { component: "CandidatesBlockNew", field: context.field },
      extra: { candidateId: context.candidateId, rawValue: String(value) },
    });
  }
  return UNKNOWN_DATE_LABEL;
}
